import { useState } from "react";
import { Field, Honeypot, SubmitButton, SuccessBlock, useSubmit, inputCls, selectCls, validPhone, validEmail } from "./fields";
import { toast } from "sonner";
import { UploadCloud, FileCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const MAX_SIZE = 5 * 1024 * 1024;
const OK_TYPES = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];

export function ResumeUpload({ file, setFile, testId }) {
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!OK_TYPES.includes(f.type) && !/\.(pdf|doc|docx)$/i.test(f.name)) return toast.error("Please upload a PDF, DOC or DOCX file.");
    if (f.size > MAX_SIZE) return toast.error("File must be under 5 MB.");
    setFile(f);
  };
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.12em] font-semibold text-ink/70 mb-1.5">Upload Resume (PDF / DOC, max 5 MB)</label>
      <label
        data-testid={testId}
        className={`flex items-center justify-center gap-3 border border-dashed rounded-sm h-24 cursor-pointer transition-colors duration-300 text-sm ${file ? "border-gold bg-gold/5 text-ink" : "border-ink/25 text-muted hover:border-gold hover:text-navy"}`}
      >
        {file ? <FileCheck size={20} className="text-gold" /> : <UploadCloud size={20} />}
        <span className="font-semibold">{file ? file.name : "Click to attach your resume"}</span>
        <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={pick} />
      </label>
      {file && (
        <button type="button" onClick={() => setFile(null)} className="text-xs text-muted hover:text-red-500 mt-1.5 transition-colors">Remove file</button>
      )}
    </div>
  );
}

export async function uploadResume(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.path;
}

const INIT = {
  full_name: "", phone: "", email: "", dob: "", gender: "", qualification: "", course: "",
  year_of_passing: "", experience: "", previous_employer: "", preferred_role: "",
  preferred_location: "", current_location: "", resume_link: "", message: "", website: "",
};

export default function ProfileForm() {
  const [f, setF] = useState(INIT);
  const [resumeFile, setResumeFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { submit, loading, success, setSuccess } = useSubmit("/profiles", "Profile received. Our recruitment team may contact you when a suitable opportunity becomes available.");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validPhone(f.phone)) return toast.error("Please enter a valid phone number.");
    if (f.email && !validEmail(f.email)) return toast.error("Please enter a valid email address.");
    let resume_path = "";
    if (resumeFile) {
      setUploading(true);
      try {
        resume_path = await uploadResume(resumeFile);
      } catch (err) {
        toast.error(formatApiError(err));
        setUploading(false);
        return;
      }
      setUploading(false);
    }
    const ok = await submit({ ...f, resume_path });
    if (ok) { setF(INIT); setResumeFile(null); }
  };

  if (success)
    return <SuccessBlock testId="profile-form-success" message="Thank you. Your profile has been received. Our recruitment team may contact you when a suitable opportunity becomes available." contactEmail="career@maqlinesolutions.com" onReset={() => setSuccess(false)} />;

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="profile-form">
      <Honeypot value={f.website} onChange={set("website")} />
      <Field label="Full Name" required><input data-testid="pf-name" className={inputCls + " w-full"} value={f.full_name} onChange={set("full_name")} required /></Field>
      <Field label="Phone" required><input data-testid="pf-phone" className={inputCls + " w-full"} value={f.phone} onChange={set("phone")} required placeholder="+91" /></Field>
      <Field label="Email"><input type="email" className={inputCls + " w-full"} value={f.email} onChange={set("email")} /></Field>
      <Field label="Date of Birth"><input type="date" className={inputCls + " w-full"} value={f.dob} onChange={set("dob")} /></Field>
      <Field label="Gender">
        <select className={selectCls} value={f.gender} onChange={set("gender")}>
          <option value="">Select</option>
          <option>Male</option><option>Female</option><option>Other</option>
        </select>
      </Field>
      <Field label="Highest Qualification" required>
        <select data-testid="pf-qualification" className={selectCls} value={f.qualification} onChange={set("qualification")} required>
          <option value="">Select qualification</option>
          {["10th", "Intermediate / PUC", "ITI", "Diploma", "B.Tech / B.E.", "Other Graduate", "Post Graduate"].map((q) => <option key={q}>{q}</option>)}
        </select>
      </Field>
      <Field label="Course / Branch"><input className={inputCls + " w-full"} value={f.course} onChange={set("course")} placeholder="e.g. Mechanical, Fitter, EEE" /></Field>
      <Field label="Year of Passing"><input className={inputCls + " w-full"} value={f.year_of_passing} onChange={set("year_of_passing")} placeholder="e.g. 2025" /></Field>
      <Field label="Experience"><input className={inputCls + " w-full"} value={f.experience} onChange={set("experience")} placeholder="e.g. Fresher / 2 years" /></Field>
      <Field label="Previous Employer"><input className={inputCls + " w-full"} value={f.previous_employer} onChange={set("previous_employer")} /></Field>
      <Field label="Preferred Job Role"><input className={inputCls + " w-full"} value={f.preferred_role} onChange={set("preferred_role")} placeholder="e.g. CNC Operator" /></Field>
      <Field label="Preferred Location"><input className={inputCls + " w-full"} value={f.preferred_location} onChange={set("preferred_location")} /></Field>
      <Field label="Current Location"><input className={inputCls + " w-full"} value={f.current_location} onChange={set("current_location")} /></Field>
      <Field label="Or Paste a Resume Link"><input className={inputCls + " w-full"} value={f.resume_link} onChange={set("resume_link")} placeholder="Google Drive / portfolio link" /></Field>
      <div className="sm:col-span-2">
        <ResumeUpload file={resumeFile} setFile={setResumeFile} testId="pf-resume-upload" />
      </div>
      <Field label="Message" className="sm:col-span-2"><textarea className={inputCls + " w-full h-24 py-2.5"} value={f.message} onChange={set("message")} /></Field>
      <div className="sm:col-span-2">
        <SubmitButton loading={loading || uploading} testId="profile-form-submit">Submit Your Profile</SubmitButton>
      </div>
    </form>
  );
}
